/**
 * Filters spots to only include those within the specified map bounds.
 * Adds a buffer zone to prevent jarring pop-in/pop-out at viewport edges.
 * @param {Array} spots - Array of spot objects
 * @param {object} bounds - Map bounds object with northEast/southWest structure or north/south/east/west properties
 * @param {number} bufferPercent - Percentage to expand bounds (default 0.2 = 20%)
 * @returns {Array} Filtered array of spots within bounds
 */
export function filterSpotsByBounds(spots, bounds, bufferPercent = 0.1) {
    if (!bounds || !spots) {
        return spots;
    }

    // Normalize bounds structure - handle both {northEast, southWest} and {north, south, east, west}
    let north;
    let south;
    let east;
    let west;

    if (bounds.northEast && bounds.southWest) {
        // Google Maps API structure
        north = bounds.northEast.latitude;
        south = bounds.southWest.latitude;
        east = bounds.northEast.longitude;
        west = bounds.southWest.longitude;
    } else {
        // Simple structure
        north = bounds.north;
        south = bounds.south;
        east = bounds.east;
        west = bounds.west;
    }

    // Validate bounds
    if (
        north === undefined ||
        south === undefined ||
        east === undefined ||
        west === undefined
    ) {
        return spots;
    }

    // Calculate buffer size
    const latBuffer = (north - south) * bufferPercent;
    const lngBuffer = (east - west) * bufferPercent;

    // Expand bounds by buffer
    const expandedBounds = {
        north: north + latBuffer,
        south: south - latBuffer,
        east: east + lngBuffer,
        west: west - lngBuffer,
    };

    const filtered = spots.filter(spot => {
        // Handle different spot data structures
        const lat =
            spot.lat ||
            spot.latitude ||
            spot.position?.lat ||
            spot.addresses?.[0]?.lat ||
            spot.addresses?.[0]?.latitude;
        const lng =
            spot.lng ||
            spot.longitude ||
            spot.position?.lng ||
            spot.addresses?.[0]?.lng ||
            spot.addresses?.[0]?.longitude;

        if (lat === undefined || lng === undefined) {
            // If we can't determine position, keep the spot

            return true;
        }

        // Check if spot is within expanded bounds
        const isInLat =
            lat >= expandedBounds.south && lat <= expandedBounds.north;
        const isInLng =
            lng >= expandedBounds.west && lng <= expandedBounds.east;
        const isInBounds = isInLat && isInLng;

        return isInBounds;
    });

    return filtered;
}
